import { useMemo } from "react";
import { Link } from "react-router-dom";
import FirmFlowTable from "../../../../components/mytasks/FirmFlowTable";
import { formatTimestampDate } from "../../../../helpers/formatDateTime";
import { formatInactiveDuration } from "../inactiveReportUtils";

const getClientId = (row) =>
    row?.clientId ?? row?.ClientId ?? row?.id ?? row?.Id;

const getClientName = (row) =>
    row?.clientName ?? row?.ClientName ?? row?.name ?? row?.Name;

const getLastActivity = (row) =>
    row?.operationalLastActivityAt ??
    row?.OperationalLastActivityAt ??
    row?.lastActivityAt ??
    row?.LastActivityAt;

const formatClientType = (value) => {
    if (value === null || value === undefined || value === "") return "—";

    const normalized = String(value).trim().toLowerCase();
    if (normalized === "1" || normalized === "individual") return "Individual";
    if (normalized === "2" || normalized === "business") return "Business";

    return String(value)
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (character) => character.toUpperCase());
};

const getDistinctNames = (value) => {
    const values = Array.isArray(value) ? value : value ? [value] : [];
    const names = values
        .map((item) => {
            if (typeof item === "string") return item.trim();

            const directName =
                item?.name ?? item?.Name ?? item?.fullName ?? item?.FullName;
            if (typeof directName === "string" && directName.trim()) {
                return directName.trim();
            }

            return `${item?.firstName ?? item?.FirstName ?? ""} ${
                item?.lastName ?? item?.LastName ?? ""
            }`.trim();
        })
        .filter(Boolean);

    return [...new Set(names)];
};

const NamesCell = ({ value }) => {
    const names = getDistinctNames(value);

    return names.length > 0 ? (
        <div className="space-y-1">
            {names.map((name) => (
                <div key={name}>{name}</div>
            ))}
        </div>
    ) : (
        <span className="text-slate-500">—</span>
    );
};

const InactiveClientsTable = ({ data, resetPageSignal }) => {
    const columns = useMemo(
        () => [
            {
                Header: "Client",
                id: "client",
                accessor: getClientName,
                Cell: (props) => {
                    const clientId = getClientId(props.row.original);
                    const name = props.value || "—";

                    return clientId ? (
                        <Link
                            to={`/clients/manage/${clientId}`}
                            className="rounded-full text-gray hover:text-blue-500"
                        >
                            {name}
                        </Link>
                    ) : (
                        <span>{name}</span>
                    );
                },
            },
            {
                Header: "Client Type",
                id: "clientType",
                accessor: (row) =>
                    row?.clientType ??
                    row?.ClientType ??
                    row?.clientDetail?.clientType,
                Cell: (props) => formatClientType(props.value),
            },
            {
                Header: "Team Leader",
                id: "teamLeader",
                accessor: (row) =>
                    row?.teamLeaders ??
                    row?.TeamLeaders ??
                    row?.teamLeaderNames ??
                    row?.teamLeaderName ??
                    row?.teamLeader,
                Cell: (props) => <NamesCell value={props.value} />,
            },
            {
                Header: "Team",
                id: "team",
                accessor: (row) =>
                    row?.teams ??
                    row?.Teams ??
                    row?.teamNames ??
                    row?.teamName ??
                    row?.team,
                Cell: (props) => <NamesCell value={props.value} />,
            },
            {
                Header: "Last Activity",
                id: "lastActivity",
                accessor: getLastActivity,
                Cell: (props) => {
                    const value = formatTimestampDate(props.value, {
                        dateStyle: "medium",
                    });
                    return value || (
                        <span className="text-slate-500">No activity recorded</span>
                    );
                },
            },
            {
                Header: "Inactive For",
                id: "inactiveFor",
                accessor: getLastActivity,
                Cell: (props) =>
                    formatInactiveDuration(props.value) || (
                        <span className="text-slate-500">—</span>
                    ),
            },
        ],
        [],
    );

    return (
        <FirmFlowTable
            columns={columns}
            data={Array.isArray(data) ? data : []}
            resetPageSignal={resetPageSignal}
        />
    );
};

export default InactiveClientsTable;
